// Interfaces/Types
import { IPartialRecalculateWashoutInput } from "@/Washouts/types/Api/commons";
import { IUser } from "@/Common/interfaces/user";
import { IAccessPositions, ISelected, IWashoutData, IWashoutQueries } from "@/Washouts/types/WashoutReview";

// MUI
import { Grid } from "@mui/material";

// Common Components
import RenderWashoutInformation from "@/Washouts/components/RenderWashoutInformation";
import UpdatedOn from "@/Washouts/components/UpdatedOn";
import { ChangeRequestSection } from "./ChangeRequestSection";
import { useWashoutReviewActions } from "@/Washouts/hooks/WashoutReview/useWashoutReviewActions";

type TInfoSectionProps = {
	selected: ISelected | null;
	washoutData?: IWashoutData;
	isLoading: boolean;
	positionToUse: string;
	positionType?: string;
	overrideCompensation: boolean;
	isApproved: boolean;
	washoutQueries: IWashoutQueries;
	isEditableWashout?: boolean;
	locationId: string;
	currentCommissionCycleId: string;
	user: IUser | null;
	accessPositions: IAccessPositions;
	onRecalculateWashout?: () => void;
};

export const InfoSection = (props: TInfoSectionProps) => {
	const {
		selected,
		washoutData,
		isLoading,
		positionToUse,
		positionType = "",
		overrideCompensation,
		isEditableWashout = false,
		isApproved,
		locationId,
		washoutQueries,
		user,
		accessPositions,
		currentCommissionCycleId,
		onRecalculateWashout,
	} = props;

	const { commissionCycleId, payPeriodId, userId, position } = selected ?? {
		commissionCycleId: "",
		payPeriodId: "",
		userId: "",
		position: "",
	};

	const { recalculateWashoutMutation, isPendingWashoutRecalculation } = useWashoutReviewActions({
		locationId,
		commissionCycleId,
		payPeriodId,
		washoutQueries,
		position,
		userId,
		onRecalculateWashout,
	});

	const handleSummaryChange = (input: IPartialRecalculateWashoutInput) => {
		if (input && locationId) {
			const params = {
				input,
				locationId,
				position: washoutData?.employee?.position ?? position,
				isDepartmental: Boolean(positionType === "Departmental"),
				washoutId: washoutData?._id ?? "",
			};
			recalculateWashoutMutation(params);
		}
	};

	if (!selected) {
		return null;
	}

	return (
		<>
			<Grid container direction="column" className="shadow-white-section" rowSpacing={0}>
				<ChangeRequestSection
					washoutData={washoutData}
					isApproved={isApproved}
					accessPositions={accessPositions}
					user={user}
					position={position}
					washoutQueries={washoutQueries}
					locationId={locationId}
					selected={selected}
					currentCommissionCycleId={currentCommissionCycleId}
				/>

				<RenderWashoutInformation
					position={positionToUse}
					positionType={positionType}
					properties={{
						overrideCompensation,
						handleSummaryChange,
						selected: { ...selected, status: "CLOSING" },
						isLoading: isLoading || isPendingWashoutRecalculation,
						editable: isEditableWashout,
						model: washoutData,
						hideEmployeeMeta: false,
						disableBoxShadow: true,
						employeeMetaProps: {
							hiddenReportButton: true,
							containerProps: {
								roundedTop: false,
							},
						},
					}}
				/>
			</Grid>

			<UpdatedOn isLoading={isLoading} updatedOn={washoutData?.created} />
		</>
	);
};

export default InfoSection;
